import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import _ from 'lodash';
import { useAnalytics } from 'use-analytics';
import { authService } from '../services';
import shapoTracker from './analyticsTracker';
import trackConversionEvent, { MILESTONES, setUserData } from './conversionEvents';
import * as proTrial from './proTrial';

// set by /connect/wordpress when the visitor has to sign in first, so they land back on the consent page after
export const PENDING_WORDPRESS_CONNECT_KEY = 'spo_wp_connect';

export function getPendingWordpressConnect() {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_WORDPRESS_CONNECT_KEY) || 'null');
    if(pending && pending.query && pending.expires > Date.now()) {
      return `/connect/wordpress?${pending.query}`;
    }
    localStorage.removeItem(PENDING_WORDPRESS_CONNECT_KEY);
  } catch(err) {
    // storage unavailable or a malformed value, nothing to resume
  }
  return null;
}

export default function useUser({ query = null, redirectTo = false, redirectIfFound = false } = {}) {
  const router = useRouter();
  const {
    data: user,
    mutate: mutateUser,
    error,
  } = useSWR('/me', authService.me, {
    dedupingInterval: 10000,
  });
  const [workspace, setWorkspace] = useState({
    id: (typeof window !== 'undefined' && window.localStorage.getItem('spo_ws')) || null,
  });
  const { track } = useAnalytics();
  useEffect(async () => {
    // if no redirect needed, just return (example: already on /dashboard)
    // if user data not yet there (fetch in progress, logged in or not) then don't do anything yet
    if(user && user.accountId && router.isReady) {
      let hasWS = false;

      const cookies = document.cookie.split('; ').reduce((acc, cookie) => {
        const [name, value] = cookie.split('=');
        acc[name] = value;
        return acc;
      }, {});

      const savedWorkspace = user.workspaces.find((ws) => ws.id === workspace.id);
      const urlWorkspace = user.workspaces.find((ws) => router.asPath.includes(ws.id));
      if(urlWorkspace) {
        setWorkspace(urlWorkspace);
        localStorage.setItem('spo_ws', urlWorkspace.id);
        hasWS = true;
      } else if(savedWorkspace) {
        setWorkspace(savedWorkspace);
        hasWS = true;
      } else {
        const defaultWS = user.workspaces[0];
        if(defaultWS) {
          setWorkspace(defaultWS);
          localStorage.setItem('spo_ws', defaultWS.id);
          hasWS = true;
        }
      }

      // redirect to /connect/wix if user has spo_wix cookie
      if(cookies?.spo_wix && workspace && hasWS) {
        return router.push(`/connect/wix?instanceId=${cookies?.spo_wix}`);
      }

      const pendingWordpressConnect = hasWS && !router.pathname.startsWith('/connect/') && getPendingWordpressConnect();
      if(pendingWordpressConnect) {
        return router.push(pendingWordpressConnect);
      }
    }
    if(user) {
      // Awaited, not fire-and-forget: the conversions below push synchronously, and the tag reads
      // user_data off the dataLayer when it fires, so this has to land first.
      await setUserData(user.email);

      const firstLoginReport = localStorage.getItem('spo_firstlogin_report');
      if(user.firstLogin && !firstLoginReport) {
        shapoTracker.alias(user.accountId);
        shapoTracker.identify(user.accountId);

        shapoTracker.trackEvent('Sign up', { ...user });
        track('signup');
        localStorage.setItem('spo_firstlogin_report', '1');

        user.firstLogin = false;

        await mutateUser(user, {
          optimisticData: user,
          rollbackOnError: true,
          populateCache: true,
          revalidate: false,
        });
      }

      if(user.widgetInstalled) {
        trackConversionEvent(MILESTONES.WIDGET_INSTALLED);
      }

      if(user.formSubmitted) {
        trackConversionEvent(MILESTONES.FORM_SUBMITTED);
      }

      if(user.surveyAnswered) {
        trackConversionEvent(MILESTONES.SURVEY_ANSWERED);
      }

      // Ads count the $1 trial checkout as a $1 purchase. The $29 that follows a week later is charged on the
      // server with no browser present, so it only reaches the server-side analytics events.
      const lastTrialReport = parseInt(localStorage.getItem('spo_trial_report'), 10);
      if(user.recentTrialStart && (!lastTrialReport || lastTrialReport < Date.now() - 3600 * 1000)) {
        // GTM reads the value from ecommerce (the GA4 tag natively, the Ads and Meta tags through DLV
        // variables); a purchase that sends none is valued at the $29 default
        track('purchase', { ecommerce: { value: proTrial.PRO_TRIAL_PRICE, currency: 'USD' }, trial: true });
        shapoTracker.trackEvent('Trial Started', { ...user });

        localStorage.setItem('spo_trial_report', `${Date.now()}`);
        user.recentTrialStart = false;

        await mutateUser(user, {
          optimisticData: user,
          rollbackOnError: true,
          populateCache: true,
          revalidate: false,
        });
      }

      const lastUpgradeReport = parseInt(localStorage.getItem('spo_pu_report'), 10);
      if(user.recentUpgrade && (!lastUpgradeReport || lastUpgradeReport < Date.now() - 3600 * 1000)) {
        track('purchase');
        shapoTracker.trackEvent('Purchase', { ...user });

        localStorage.setItem('spo_pu_report', `${Date.now()}`);
        user.recentUpgrade = false;

        await mutateUser(user, {
          optimisticData: user,
          rollbackOnError: true,
          populateCache: true,
          revalidate: false,
        });
      }
    }

    if(!redirectTo || !user) {
      return;
    }

    if(
      // If redirectTo is set, redirect if the user was not found.
      (redirectTo && !redirectIfFound && !user?.accountId)
      // If redirectIfFound is also set, redirect if the user was found
      || (redirectIfFound && user?.accountId)
    ) {
      const topath = { pathname: redirectTo };
      if(!_.isEmpty(query)) {
        topath.query = query;
      }
      router.push(topath);
    }
  }, [user, redirectIfFound, redirectTo, router.asPath, router.isReady, error]);

  return { user, mutateUser, workspace, error };
}
